import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { CasebookResponse, LearnerViewInPlay } from "@pushback-and-chaos/contracts";
import type { CaseOpening } from "@pushback-and-chaos/client";
import { CASEBOOK } from "../design/copy.js";
import { apiClient } from "../data/transport.js";
import { retryDelay, retryTransient } from "../data/retry.js";
import { toProse } from "../data/adapt.js";
import { PlaySurface, type PlayBook, type PlayPrologue } from "./play.js";
import { casebookPages, endingByline } from "./storyteller/casebook-atoms.js";

/**
 * THE CASEBOOK (2026-09-24, Andre): a finished engagement read back as a book.
 * The play surface itself, handed the whole story at once and a `book`, so it
 * reads as the same book, closed: title page and prologue first, then the story
 * chapter by chapter, then how it ended, the recommendation, the examination
 * and the review. Nothing on it can add to the story.
 */
export function CasebookScreen({
  sessionId,
  onTape,
  onTryAgain,
}: {
  readonly sessionId: string;
  /** "the tape.": the debrief of this engagement, where the mechanics are disclosed in full. */
  readonly onTape: (sessionId: string) => void;
  /** "try the case again.": another engagement of the same case; this one is kept. */
  readonly onTryAgain: (caseSlug: string) => void;
}): ReactNode {
  const client = apiClient();
  const book = useQuery({
    queryKey: ["casebook", sessionId],
    queryFn: () => client.getCasebook(sessionId),
    staleTime: 60_000,
    retry: retryTransient,
    retryDelay,
  });
  const slug = book.data?.caseSlug ?? null;
  const opening = useQuery({
    queryKey: ["opening", slug],
    enabled: slug !== null,
    queryFn: () => client.getCaseOpening(slug ?? ""),
    staleTime: Number.POSITIVE_INFINITY,
  });

  if (book.isError) return <p className="pcs-book-status">{CASEBOOK.failed}</p>;
  // Wait for the opening too (or its failure), so the book does not open on the story and then jump to its title page.
  if (book.data === undefined || opening.isPending) return <p className="pcs-book-status">{CASEBOOK.loading}</p>;
  return <OpenBook book={book.data} opening={opening.data ?? null} onTape={onTape} onTryAgain={onTryAgain} />;
}

function OpenBook({
  book,
  opening,
  onTape,
  onTryAgain,
}: {
  readonly book: CasebookResponse;
  readonly opening: CaseOpening | null;
  readonly onTape: (sessionId: string) => void;
  readonly onTryAgain: (caseSlug: string) => void;
}): ReactNode {
  const pages = useMemo(() => casebookPages(book), [book]);
  const playBook: PlayBook = useMemo(
    () => ({
      items: () => pages.items,
      chapters: () => pages.chapters,
      reviewKey: pages.reviewKey,
      sceneFor: pages.sceneFor,
      documentsFor: pages.documentsFor,
      allDocuments: () => pages.allDocuments,
      titleNote: endingByline(book.ending, book.completedAt),
      onTape: () => { onTape(book.sessionId); },
      onTryAgain: () => { onTryAgain(book.caseSlug); },
    }),
    [pages, book, onTape, onTryAgain],
  );
  // The title page is the book's own: the case's name and question from the book, its prologue from the opening.
  const prologue: PlayPrologue = {
    slug: book.caseSlug,
    client: book.client,
    industry: book.industry,
    question: book.engagementQuestion,
    pages: (opening?.prologuePages ?? []).map((page) => ({
      pageRef: String(page.index),
      eyebrow: page.eyebrow,
      title: page.title,
      heroImageSlot: page.heroImageSlot,
      blocks: page.blocks.map((b) => ({ kind: b.kind, text: b.text })),
    })),
  };
  const view: LearnerViewInPlay = useMemo(
    () => ({
      engagement_question: toProse(book.engagementQuestion),
      narration: [],
      documents: [...pages.allDocuments],
      facts: [],
      npcs: [],
      open_threads: [],
      hypotheses_on_record: [],
      ruling_ledger: [],
      situation_pressure: "CALM",
      current_beat: { id: "casebook", title: toProse("") },
    }),
    [book, pages],
  );
  const lastScene = pages.sceneFor(null);

  return (
    <PlaySurface
      sessionId={book.sessionId}
      view={view}
      thread={[]}
      pendingText={null}
      hypotheses={[]}
      notes={{ established: [], open: [] }}
      npcTells={[]}
      statuses={[]}
      connection="live"
      resumedFromTurn={null}
      busy={false}
      failure={null}
      blockedReason={null}
      canRecommend={false}
      orientationNoteDocId={null}
      mentorAsk={null}
      composerPlaceholder=""
      environment={lastScene.environment}
      environmentPlan={null}
      dictate={null}
      narrationAudio={null}
      readAloud={false}
      prologue={prologue}
      book={playBook}
      submit={noop}
      retry={noop}
      openSettings={noop}
      openRecommend={noop}
    />
  );
}

function noop(): void {
  /* a closed book takes no turns */
}
